import { lazy, Suspense, type ReactNode } from 'react'

import type { ApiError } from './api-error'
import { AuthContext, type AuthContextValue } from './auth-context'
import { getRuntimeConfig } from './config'
import { MockAuthProvider } from './mock-auth-provider'

// MSAL ist groß und wird im Mock-Modus nie gebraucht. Der Lazy-Import hält es
// aus dem Hauptbundle heraus; im Entra-Modus lädt es direkt beim Start nach.
const loadEntraAuthProvider = () =>
  import('./entra-auth-provider').then((loaded) => ({
    default: loaded.EntraAuthProvider,
  }))

const EntraAuthProvider = lazy(loadEntraAuthProvider)

/**
 * Fordert den Anmelde-Code an, ohne auf das erste Rendern zu warten.
 *
 * Ohne das beginnt der Abruf erst, wenn React den Suspense-Rand in
 * {@link AuthProvider} erreicht — also nach Aufbau von Abfrage-Client, Router
 * und Oberflächenbaum. Und alles Weitere hängt daran: MSAL laden, Instanz
 * erzeugen, Konto wiederherstellen, Token holen, Rollen abrufen. Diese Kette
 * läuft zwingend nacheinander, deshalb zählt jede Runde am Anfang doppelt.
 *
 * Die Shell ruft das einmal beim Start auf. Mehrfachaufrufe sind harmlos —
 * der Bündler liefert dasselbe Versprechen zurück.
 */
export function preloadAuth(): void {
  try {
    if (getRuntimeConfig().authMode !== 'entra') return
  } catch {
    // Fehlkonfiguration: AuthProvider zeigt sie beim Rendern lesbar an. Hier
    // darf sie den Start nicht abbrechen.
    return
  }
  void loadEntraAuthProvider()
}

export interface AuthProviderProps {
  children: ReactNode
  /**
   * Wird aufgerufen, wenn ein API-Aufruf mit 401 oder 403 antwortet. Die Shell
   * reagiert darauf zentral (neu anmelden bzw. Hinweis „keine Berechtigung"),
   * damit Module das nicht jedes Mal selbst behandeln müssen.
   */
  onApiAuthError?: (error: ApiError, reauthenticate: () => void) => void
}

/**
 * Stellt Anmeldung und Rollen bereit. Die Shell setzt den Provider einmal ganz
 * außen; Module verwenden nur die Hooks.
 *
 * Welcher Modus läuft, entscheidet `VITE_AUTH_MODE`:
 * `mock` (Standard beim Entwickeln) oder `entra`. Beide Modi haben exakt
 * dieselbe API — Modulcode sieht keinen Unterschied.
 *
 * @example
 * ```tsx
 * <AuthProvider onApiAuthError={handleAuthError}>
 *   <RouterProvider router={router} />
 * </AuthProvider>
 * ```
 */
export function AuthProvider({ children, onApiAuthError }: AuthProviderProps) {
  let authMode: ReturnType<typeof getRuntimeConfig>['authMode']
  try {
    authMode = getRuntimeConfig().authMode
  } catch (cause) {
    const error = cause instanceof Error ? cause : new Error(String(cause))
    const value: AuthContextValue = {
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error,
      mode: 'entra',
      login: () => {},
      logout: () => {},
      retryAuthentication: () => window.location.reload(),
      getAccessToken: async () => null,
      reportApiAuthError: () => {},
    }
    return <AuthContext value={value}>{children}</AuthContext>
  }

  if (authMode === 'entra') {
    return (
      <Suspense fallback={null}>
        <EntraAuthProvider {...(onApiAuthError ? { onApiAuthError } : {})}>
          {children}
        </EntraAuthProvider>
      </Suspense>
    )
  }

  return (
    <MockAuthProvider {...(onApiAuthError ? { onApiAuthError } : {})}>
      {children}
    </MockAuthProvider>
  )
}
